/**
 * AddCountdownDialog - Dialog for adding/editing inline countdown timers
 *
 * Provides name + mode (duration/targetDate) + value + format inputs.
 * Follows AddLinkDialog pattern.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import type { InlineCountdownConfig, InlineCountdownFormat, InlineCountdownMode } from '@segmently/schema-models/v2';

/**
 * Props for AddCountdownDialog
 */
export interface AddCountdownDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (name: string, config: InlineCountdownConfig) => void;
  existingKeys: string[];
  mode?: 'add' | 'edit';
  editingKey?: string;
  initialConfig?: InlineCountdownConfig;
}

const FORMAT_OPTIONS: { value: InlineCountdownFormat; label: string }[] = [
  { value: 'MM:SS', label: 'MM:SS (e.g. 05:00)' },
  { value: 'HH:MM:SS', label: 'HH:MM:SS (e.g. 01:30:00)' },
  { value: 'DD:HH:MM:SS', label: 'DD:HH:MM:SS (e.g. 02:12:00:00)' },
];

export const AddCountdownDialog: React.FC<AddCountdownDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingKeys,
  mode: dialogMode = 'add',
  editingKey,
  initialConfig,
}) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [countdownMode, setCountdownMode] = useState<InlineCountdownMode>('duration');
  const [duration, setDuration] = useState(300); // 5 minutes default
  const [targetDate, setTargetDate] = useState('');
  const [format, setFormat] = useState<InlineCountdownFormat>('MM:SS');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      if (dialogMode === 'edit' && editingKey && initialConfig) {
        setName(editingKey);
        setNameError(null);
        setCountdownMode(initialConfig.mode);
        setDuration(initialConfig.duration ?? 300);
        setTargetDate(
          initialConfig.targetDate
            ? new Date(initialConfig.targetDate).toISOString().slice(0, 16)
            : ''
        );
        setFormat(initialConfig.format);
      } else {
        setName('');
        setNameError(null);
        setCountdownMode('duration');
        setDuration(300);
        setTargetDate('');
        setFormat('MM:SS');
      }
    }
  }, [open, dialogMode, editingKey, initialConfig]);

  // Validate name
  const validateName = useCallback(
    (value: string): string | null => {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const keysToCheck =
        dialogMode === 'edit' && editingKey
          ? existingKeys.filter((k) => k !== editingKey)
          : existingKeys;
      if (keysToCheck.includes(trimmed)) {
        return `"${trimmed}" already exists`;
      }
      return null;
    },
    [existingKeys, dialogMode, editingKey]
  );

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setName(value);
      setNameError(validateName(value));
    },
    [validateName]
  );

  const handleModeChange = useCallback((event: SelectChangeEvent<InlineCountdownMode>) => {
    setCountdownMode(event.target.value as InlineCountdownMode);
  }, []);

  const handleDurationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(event.target.value, 10);
    if (!isNaN(val) && val >= 0) {
      setDuration(val);
    }
  }, []);

  const handleTargetDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTargetDate(event.target.value);
  }, []);

  const handleFormatChange = useCallback((event: SelectChangeEvent<InlineCountdownFormat>) => {
    setFormat(event.target.value as InlineCountdownFormat);
  }, []);

  // Check form validity
  const isValid = useMemo(() => {
    const hasName = name.trim().length > 0;
    const hasNoNameError = !nameError;
    if (countdownMode === 'duration') {
      return hasName && hasNoNameError && duration > 0;
    }
    return hasName && hasNoNameError && targetDate.length > 0;
  }, [name, nameError, countdownMode, duration, targetDate]);

  // Helper to display duration in human-readable format
  const durationLabel = useMemo(() => {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    if (m > 0 && s > 0) return `${m} min ${s} sec`;
    if (m > 0) return `${m} min`;
    return `${s} sec`;
  }, [duration]);

  const handleSubmit = useCallback(() => {
    if (!isValid) return;
    const config: InlineCountdownConfig = {
      mode: countdownMode,
      format,
      ...(countdownMode === 'duration'
        ? { duration }
        : { targetDate: new Date(targetDate).toISOString() as unknown as Date }),
    };
    onAdd(name.trim(), config);
    onClose();
  }, [isValid, name, countdownMode, format, duration, targetDate, onAdd, onClose]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isValid) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [isValid, handleSubmit]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>{dialogMode === 'edit' ? 'Edit Countdown' : 'Add Countdown'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Timer Name"
            placeholder="e.g., offerTimer, saleCountdown..."
            value={name}
            onChange={handleNameChange}
            onKeyPress={handleKeyPress}
            error={!!nameError}
            helperText={nameError}
            size="small"
            data-testid="countdown-name-input"
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Timer Mode</InputLabel>
            <Select<InlineCountdownMode>
              value={countdownMode}
              onChange={handleModeChange}
              label="Timer Mode"
            >
              <MenuItem value="duration">Fixed Duration</MenuItem>
              <MenuItem value="targetDate">Target Date</MenuItem>
            </Select>
          </FormControl>

          {countdownMode === 'duration' ? (
            <TextField
              fullWidth
              label="Duration (seconds)"
              type="number"
              value={duration}
              onChange={handleDurationChange}
              onKeyPress={handleKeyPress}
              size="small"
              helperText={durationLabel}
              inputProps={{ min: 1, max: 86400 }}
              data-testid="countdown-duration-input"
            />
          ) : (
            <TextField
              fullWidth
              label="Target Date & Time"
              type="datetime-local"
              value={targetDate}
              onChange={handleTargetDateChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>Display Format</InputLabel>
            <Select<InlineCountdownFormat>
              value={format}
              onChange={handleFormatChange}
              label="Display Format"
            >
              {FORMAT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid} data-testid="countdown-submit-btn">
          {dialogMode === 'edit' ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCountdownDialog;
